import Vue from 'vue'
import { addNamespace } from './utils'
import { getPdfAssignment } from '@/apis/pdf-assignments'
import { hasProperty } from '@/utils/object'

/**
 * @file
 * @author Thomas Grinderslev <thomas@edulab.dk>
 */

/**
 * @typedef {{pdfAssignments: Object<id, Object>}} PdfAssignmentsStoreState
 */

/**
 * The types used in this store
 * @enum {string}
 */
export const PdfAssignmentsStoreTypes = {
  getters: {
    GET_PDF_ASSIGNMENT: 'getPdfAssignment',
    HAS_PDF_ASSIGNMENT_DETAILS: 'hasPdfAssignmentsDetails',
  },
  actions: {
    FETCH_PDF_ASSIGNMENT: 'fetchPdfAssignment',
    SET_PDF_ASSIGNMENT: 'setPdfAssignment',
  },
  mutations: {
    SET_PDF_ASSIGNMENT: 'setPdfAssignment',
  },
}

/**
 * A namespaced version of the types used in this store
 * @enum {string}
 */
export const PdfAssignmentsStoreNamespacedTypes = addNamespace(
  'pdfAssignments',
  PdfAssignmentsStoreTypes
)

/**
 * @returns {PdfAssignmentsStoreState}
 */
export function state() {
  return {
    pdfAssignments: {},
  }
}

export const getters = {
  /**
   * @param {PdfAssignmentsStoreState} state
   * @returns {function(number):Object}
   */
  [PdfAssignmentsStoreTypes.getters.GET_PDF_ASSIGNMENT]: state => id => {
    return state.pdfAssignments[id] || null
  },

  /**
   * @param {PdfAssignmentsStoreState} state
   * @returns {function(number):boolean}chapters_pdfAssignments
   */
  [PdfAssignmentsStoreTypes.getters.HAS_PDF_ASSIGNMENT_DETAILS]: (
    state,
    getters
  ) => id => {
    return hasProperty(
      getters[PdfAssignmentsStoreTypes.getters.GET_PDF_ASSIGNMENT](id),
      'topics'
    )
  },
}

export const actions = {
  /**
   * @param {VuexCommit} commit
   * @param {VuexGetters} getters
   * @param {number} pdfAssignmentsId
   * @returns {Promise}
   */
  [PdfAssignmentsStoreTypes.actions.FETCH_PDF_ASSIGNMENT](
    { commit, getters },
    pdfAssignmentsId
  ) {
    if (
      getters[PdfAssignmentsStoreTypes.getters.HAS_PDF_ASSIGNMENT_DETAILS](
        pdfAssignmentsId
      )
    ) {
      return Promise.resolve()
    }

    return getPdfAssignment(pdfAssignmentsId).then(pdfAssignment => {
      commit(
        PdfAssignmentsStoreTypes.mutations.SET_PDF_ASSIGNMENT,
        pdfAssignment
      )
    })
  },
}

export const mutations = {
  /**
   * @param {PdfAssignmentsStoreState} state
   * @param {Object} pdfAssignments
   */
  [PdfAssignmentsStoreTypes.mutations.SET_PDF_ASSIGNMENT](
    state,
    pdfAssignments
  ) {
    Vue.set(state.pdfAssignments, pdfAssignments.id, pdfAssignments)
  },
}

export default {
  namespaced: true,
  Types: PdfAssignmentsStoreTypes,
  NamespacedTypes: PdfAssignmentsStoreNamespacedTypes,
  state,
  getters,
  actions,
  mutations,
}
